import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ConfirmationPopup,
  ContentLoader,
  IconDelete,
  List,
  Notification,
} from 'shared/components';
import { displayValue, hasPermission } from 'shared/helpers';
import { fetchAccountManagers, removeAccountManager } from '../../../actions';
import AddAccountManagerModal from './AddAccountManagerModal';
import '../../styles.scss';

const PAGE_SIZE = 20;

const LicenseManagersTab = ({ accountId }) => {
  const { t } = useTranslation();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPermissionError, setIsPermissionError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([]);
  const [retryToken, setRetryToken] = useState(0);
  const [managerToRemove, setManagerToRemove] = useState(null);
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [showAddManagerModal, setShowAddManagerModal] = useState(false);
  const canManageManagers = hasPermission('manage_managers');

  useEffect(() => {
    setPage(0);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let isStale = false;
    setLoading(true);
    setError(false);
    setErrorMessage('');
    setIsPermissionError(false);
    fetchAccountManagers(accountId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : get(payload, 'count', results.length);
        if (!isStale) {
          setManagers(results);
          setCount(totalCount);
          setLoading(false);
        }
      })
      .catch((fetchError) => {
        if (!isStale) {
          const errorDetail = get(fetchError, 'response.data.detail', '');
          const isForbidden =
            get(fetchError, 'response.status') === 403 ||
            errorDetail ===
              'You do not have permission to perform this action.';
          setIsPermissionError(isForbidden);
          setErrorMessage(errorDetail);
          setError(true);
          setManagers([]);
          setCount(0);
          setLoading(false);
        }
      });
  }, [accountId, currentSort, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const handleAddManager = () => {
    if (!canManageManagers) {
      return false;
    }
    setShowAddManagerModal(true);
    return true;
  };

  const handleRemoveManager = () => {
    const managerId = get(managerToRemove, 'id');
    if (!managerId) return;
    setRemoveLoading(true);
    removeAccountManager(accountId, managerId)
      .then(() => {
        const shouldGoToPreviousPage = page > 0 && managers.length === 1;
        if (shouldGoToPreviousPage) {
          setPage((prevPage) => Math.max(prevPage - 1, 0));
        } else {
          setRetryToken((token) => token + 1);
        }
        setRemoveLoading(false);
        setManagerToRemove(null);
        setShowRemovePopup(false);
        Notification('success', t('Changes saved successfully'));
      })
      .catch(() => {
        setRemoveLoading(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      });
  };

  const columns = [
    {
      Header: t('Email'),
      accessor: 'true_email',
      Cell: (row) => displayValue(row.value),
    },
    {
      Header: t('First Name'),
      accessor: 'first_name',
      Cell: (row) => displayValue(row.value),
    },
    {
      Header: t('Last Name'),
      accessor: 'last_name',
      Cell: (row) => displayValue(row.value),
    },
    {
      Header: t('Phone Number'),
      accessor: 'phone_number',
      Cell: (row) => displayValue(row.value, '-'),
    },
    ...(canManageManagers
      ? [
          {
            Cell: (rowData) => (
              <Button
                className="edit-button"
                onClick={() => {
                  setManagerToRemove(rowData.original);
                  setShowRemovePopup(true);
                }}
                type="button"
                disabled={removeLoading}
              >
                <IconDelete height="16px" width="16px" color="#ee5253" />
              </Button>
            ),
            maxWidth: 50,
          },
        ]
      : []),
  ];

  if (loading) {
    return <ContentLoader text={t('Loading license managers')} />;
  }

  if (error) {
    return (
      <div className="CustomerAccount-error">
        <p>
          {errorMessage ||
            t('Unable to load license managers for this account')}
        </p>
        {!isPermissionError && (
          <Button theme="info" size="sm" onClick={handleRetry}>
            {t('Retry')}
          </Button>
        )}
      </div>
    );
  }

  if (!managers.length) {
    return (
      <div className="CustomerAccount-list">
        {canManageManagers && (
          <Button theme="default" size="sm" onClick={handleAddManager}>
            {t('Add manager')}
          </Button>
        )}
        <div className="CustomerAccount-empty">
          {t('No license managers for this account')}
        </div>
        {showAddManagerModal && (
          <AddAccountManagerModal
            accountId={accountId}
            closeCb={() => setShowAddManagerModal(false)}
            confirmCb={() => {
              handleRetry();
              setShowAddManagerModal(false);
            }}
          />
        )}
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="CustomerAccount-list">
      {canManageManagers && (
        <Button theme="default" size="sm" onClick={handleAddManager}>
          {t('Add manager')}
        </Button>
      )}

      <List
        data={managers}
        columns={columns}
        page={page}
        pages={pages}
        manual
        minRows={managers.length || 5}
        showPagination={pages > 1}
        loading={loading}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
        defaultPageSize={PAGE_SIZE}
      />
      {showRemovePopup && (
        <ConfirmationPopup
          closeCb={() => {
            setManagerToRemove(null);
            setShowRemovePopup(false);
          }}
          confirmCb={handleRemoveManager}
          title={t('Are you sure you want to remove this license manager?')}
          confirmText={t('Remove')}
          theme="error"
          disabled={removeLoading}
        >
          {get(managerToRemove, 'true_email')}
        </ConfirmationPopup>
      )}
      {showAddManagerModal && (
        <AddAccountManagerModal
          accountId={accountId}
          closeCb={() => setShowAddManagerModal(false)}
          confirmCb={() => {
            handleRetry();
            setShowAddManagerModal(false);
          }}
        />
      )}
    </div>
  );
};

LicenseManagersTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseManagersTab;
